import { FunctionComponent, PropsWithChildren } from 'react';
import { allEnvironments } from '@/models/product';
import { v4 as uuid } from 'uuid';
import { useAuthentication } from '@/hooks/useAuthentication';
import { useNotification } from '@starjack/features/notifications';
import { ConditionLink, PriceCondition } from '@/models/conditionLink';
import { usePostOrPutConditionLinkMutation } from '@/api/v2/conditionLinks';
import { PriceConditionForm } from '@/pages/rulesets/components/conditions/PriceConditionForm';

const buildInitialPriceCondition = (): PriceCondition => ({
    id: 'new-' + uuid(),
    name: '',
    externalName: {
        de: '',
        en: '',
    },
    sort: undefined,
    minDaysBeforeTicketStart: undefined,

    validFrom: null,
    validTo: null,
    startDateValidFrom: null,
    startDateValidTo: null,
    validWeekdays: new Set<string>(),
    startDateValidWeekdays: new Set<string>(),
    startDateContingentFrom: null,
    startDateContingentTo: null,
    conditionContingentFrom: null,
    conditionContingentTo: null,
    personTypeIds: [],
    sellingGroup: null,
    notSellingGroup: null,
    priceChange: { amount: 0, currency: 'EUR' },
    priceChangeAbsolute: false,
    blockCancellation: false,
    dayContingentFrom: null,
    dayContingentTo: null,
    validityPeriodContingentFrom: null,
    validityPeriodContingentTo: null,
    roundTo: null,
    round50: false,
    environments: allEnvironments,
    contingentsProductIdSpecific: true,
    calculateRefundableProductVariant: false,
    userIds: [],
    useCatalogPrice: false,
});

type PriceConditionFormContainerProps = {
    onClose: () => void;
    conditionLink: ConditionLink;
    condition?: PriceCondition;
    onUpdateConditionLink: (conditionLink: ConditionLink) => void;
    createNewConditionOptions?: {
        insertAtIndex?: number;
    };
};

export const PriceConditionFormContainer: FunctionComponent<
    PropsWithChildren<PriceConditionFormContainerProps>
> = ({
    condition,
    conditionLink,
    onClose,
    onUpdateConditionLink,
    createNewConditionOptions = {},
}) => {
    const {
        liftOperatorData: { liftOperatorId },
    } = useAuthentication();
    const { addNotification } = useNotification();
    const [apiAction, { isLoading }] = usePostOrPutConditionLinkMutation({
        liftOperatorId,
        conditionLink,
    });

    const isNewCondition = !condition?.id;

    // save or update logic
    const saveOrUpdate = (nextCondition: PriceCondition) => {
        const nextConditionLink = {
            ...conditionLink,
        };
        if (!isNewCondition) {
            const conditionIndex = conditionLink.priceConditions.findIndex(
                (c) => condition.id === c.id
            );

            if (conditionIndex > -1) {
                const priceConditions = [...conditionLink.priceConditions];

                priceConditions.splice(conditionIndex, 1, nextCondition);

                nextConditionLink.priceConditions = priceConditions;
            }
        } else {
            const positionToInsert =
                createNewConditionOptions.insertAtIndex > -1
                    ? createNewConditionOptions.insertAtIndex
                    : nextConditionLink.priceConditions.length;

            const nextConditions = [...nextConditionLink.priceConditions];
            nextConditions.splice(positionToInsert, 0, nextCondition);

            nextConditionLink.priceConditions = nextConditions.map(
                (nC, index) => ({
                    ...nC,
                    sort: index,
                })
            );
        }

        apiAction({ liftOperatorId, conditionLink: nextConditionLink })
            .unwrap()
            .then((nextConditionLink) => {
                addNotification({
                    message: 'Regel gespeichert',
                    type: 'success',
                });
                onUpdateConditionLink(nextConditionLink);
                onClose();
            })
            .catch(() => {
                addNotification({
                    message: 'Fehler beim speichern',
                    type: 'error',
                });
            });
    };

    return (
        <PriceConditionForm
            condition={condition || buildInitialPriceCondition()}
            isNewCondition={isNewCondition}
            onSave={saveOrUpdate}
            onClose={onClose}
            isLoading={isLoading}
        />
    );
};
